<template>
  <!-- PAC-1387 — reached from `status === "verify-email"`: the visitor signed
       up through Auth0 and the backend will not bind an unproven address. This
       used to be a raw "GET /user-access/organisation failed: 403" line. -->
  <PreAuthPage>
    <div class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <span class="verify__badge"><v-icon size="20" color="primary">mark_email_unread</v-icon></span>
      <h1 class="sh-card__title">{{ t("auth.verify.title") }}</h1>
      <p class="sh-card__subtitle">
        <template v-if="email">{{ t("auth.verify.body", { email }) }}</template>
        <template v-else>{{ t("auth.verify.bodyNoEmail") }}</template>
      </p>
    </div>

    <p v-if="notice" class="verify__notice" :class="`verify__notice--${notice.tone}`" role="status">
      <v-icon size="15">{{ notice.tone === "error" ? "error_outline" : "info_outline" }}</v-icon>
      <span>{{ notice.text }}</span>
    </p>

    <v-btn
      class="verify__continue"
      color="primary"
      size="large"
      variant="flat"
      block
      :loading="continuing"
      :disabled="resending"
      @click="onContinue"
      >{{ t("auth.verify.continue") }}</v-btn>

    <v-btn
      class="verify__resend"
      variant="outlined"
      block
      :loading="resending"
      :disabled="continuing || cooldown > 0"
      @click="onResend"
      >{{ cooldown > 0 ? t("auth.verify.resendIn", { n: cooldown }) : t("auth.verify.resend") }}</v-btn>

    <p class="verify__hint">{{ t("auth.verify.spamHint") }}</p>

    <!-- Sign-out clears the Auth0 session too, so the next "Sign in" asks for
         credentials — the way out for a mistyped address. -->
    <v-btn
      class="verify__switch"
      variant="text"
      size="small"
      block
      :disabled="continuing"
      @click="session.signOut()"
      >{{ t("auth.differentAccount") }}</v-btn>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";

/** Fallback when the server answers TOO_SOON without a number. */
const DEFAULT_COOLDOWN_SECONDS = 60;
const TICK_MS = 1000;

interface Notice {
  tone: "info" | "error";
  text: string;
}

export default defineComponent({
  name: "VerifyEmail",
  components: { LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);
    const email = computed(() => session.user?.email || null);

    const continuing = ref(false);
    const resending = ref(false);
    const notice = ref<Notice | null>(null);
    const cooldown = ref(0);
    let timer: ReturnType<typeof setInterval> | null = null;

    function startCooldown(seconds: number): void {
      cooldown.value = Math.max(0, Math.round(seconds));
      if (timer) clearInterval(timer);
      timer = setInterval(() => {
        cooldown.value = Math.max(0, cooldown.value - 1);
        if (cooldown.value === 0 && timer) {
          clearInterval(timer);
          timer = null;
        }
      }, TICK_MS);
    }
    onUnmounted(() => {
      if (timer) clearInterval(timer);
    });

    async function onContinue(): Promise<void> {
      continuing.value = true;
      notice.value = null;
      try {
        const verified = await session.continueAfterEmailVerification();
        // Still unverified and still on this screen: say so, rather than
        // looking like the button did nothing. (When the store re-ran the
        // Auth0 redirect instead, the page is already navigating away.)
        if (!verified && session.status === "verify-email") {
          notice.value = { tone: "info", text: t("auth.verify.stillUnverified") };
        }
      } catch {
        notice.value = { tone: "error", text: t("auth.verify.continueFailed") };
      } finally {
        continuing.value = false;
      }
    }

    async function onResend(): Promise<void> {
      resending.value = true;
      notice.value = null;
      try {
        const result = await session.resendEmailVerification();
        if (result.status === "SENT") {
          notice.value = { tone: "info", text: t("auth.verify.sent") };
          startCooldown(result.retryAfterSeconds ?? DEFAULT_COOLDOWN_SECONDS);
        } else if (result.status === "TOO_SOON") {
          notice.value = { tone: "info", text: t("auth.verify.tooSoon") };
          startCooldown(result.retryAfterSeconds ?? DEFAULT_COOLDOWN_SECONDS);
        } else {
          // ALREADY_VERIFIED — they clicked the link elsewhere; carry on.
          await onContinue();
        }
      } catch {
        notice.value = { tone: "error", text: t("auth.verify.resendFailed") };
      } finally {
        resending.value = false;
      }
    }

    return {
      t,
      session,
      tenantName,
      branding,
      email,
      continuing,
      resending,
      notice,
      cooldown,
      onContinue,
      onResend,
    };
  },
});
</script>

<style scoped>
.verify__badge {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.14);
}
.verify__notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 1.4;
}
.verify__notice--info {
  color: var(--sh-text-60);
}
.verify__notice--error {
  color: rgb(var(--v-theme-error));
}
.verify__continue {
  font-weight: 600;
}
.verify__resend {
  margin-top: 8px;
}
.verify__hint {
  margin: 10px 0 0;
  font-size: 12px;
  color: var(--sh-text-60);
  text-align: center;
}
.verify__switch {
  margin-top: 4px;
  font-size: 12px;
  color: var(--sh-text-60);
}
</style>
